import { darken, lighten, Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { CSSProperties } from 'react';
import { bloxColors, bloxValues, fontWeightMedium } from '../../../themes/constants';
import { radiusHalf } from 'utility/borderRadius';

export const spaceVal = 1.5; // 1.5 mui = 12px for margin, gaps, etc around the grid and in the cards.

export type PossibleStatuses =
  | 'active'
  | 'active and canceled'
  | 'cancel'
  | 'canceled'
  | 'cancelled'
  | 'critical'
  | 'debug'
  | 'disabled'
  | 'error'
  | 'expired'
  | 'former'
  | 'gateway_processing_failed'
  | 'gateway_processing_result_unknown'
  | 'gateway_setup_failed'
  | 'grace'
  | 'info'
  | 'never'
  | 'processing'
  | 'succeeded'
  | 'success'
  | 'warning'
  | 'paid'
  | 'refunded'
  | 'pending'
  | 'failed'
  | string
  | undefined;

/** Return object for status severity styling, so you can destructure/dot-access what you need */
export interface StatusStylesReturn {
  backgroundColor?: CSSProperties['backgroundColor'];
  color?: CSSProperties['color'];
}
/** Helper function subscriber / activity severity styling. */
export const getStatusStyles = (
  theme: Theme,
  status: PossibleStatuses,
  colorTextDarkMode?: boolean,
): StatusStylesReturn => {
  const isDarkMode = theme.palette.mode === 'dark';
  const bgDarkVal = 0.45;
  const bgLightVal = 0.75;
  const colorLightVal = 0.45;
  const colorTxtDarkVal = 0.75;

  const green = bloxColors.green;
  const yellow = bloxColors.yellow;
  const red = bloxColors.red;
  const darkgray = bloxColors.darkgray;
  const lightgray = bloxColors.lightgray;

  switch (status) {
    case 'success':
    case 'active':
    case 'paid':
    case 'succeeded': {
      return {
        backgroundColor: isDarkMode
          ? darken(green, bgDarkVal)
          : lighten(green, bgLightVal),
        color:
          colorTextDarkMode && isDarkMode
            ? lighten(green, colorTxtDarkVal) // dark mode if colorTextDarkMode is true
            : isDarkMode
            ? theme.palette.text.primary // dark mode default text color
            : darken(green, colorLightVal), // light mode default text color
      };
    }
    case 'warning':
    case 'processing':
    case 'pending':
    case 'grace': {
      return {
        backgroundColor: isDarkMode
          ? darken(yellow, bgDarkVal)
          : lighten(yellow, bgLightVal),
        color:
          colorTextDarkMode && isDarkMode
            ? lighten(yellow, colorTxtDarkVal)
            : isDarkMode
            ? theme.palette.text.primary
            : darken(yellow, colorLightVal),
      };
    }
    case 'error':
    case 'critical':
    case 'expired':
    case 'former':
    case 'canceled':
    case 'cancelled':
    case 'failed':
    case 'gateway_processing_failed':
    case 'active and canceled':
    case 'gateway_setup_failed':
    case 'refunded':
    case 'cancel': {
      return {
        backgroundColor: isDarkMode ? darken(red, bgDarkVal) : lighten(red, bgLightVal),
        color:
          colorTextDarkMode && isDarkMode
            ? lighten(red, colorTxtDarkVal)
            : isDarkMode
            ? theme.palette.text.primary
            : darken(red, colorLightVal),
      };
    }
    case 'debug':
    case 'info':
    case 'disabled':
    case 'gateway_processing_result_unknown':
    case 'never': {
      return {
        backgroundColor: isDarkMode ? darkgray : lightgray,
        color: theme.palette.text.secondary,
      };
    }
    default: {
      return {
        backgroundColor: isDarkMode ? darkgray : lightgray,
        color: theme.palette.text.secondary,
      };
    }
  }
};

// Add to the sx of StatusText to get a badge/pill look.
export const statusTextBadgeTypography: SystemStyleObject = {
  width: 'max-content',
  textAlign: 'center',
  padding: '2px 8px',
  borderRadius: '4px',
  lineHeight: 'normal',
  fontSize: bloxValues.smallFontSizeRem,
  fontWeight: fontWeightMedium,
  '&:first-letter': {
    textTransform: 'capitalize',
  },
};

export const infoTypographySx = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  p: spaceVal,
  my: spaceVal,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.action.hover}`,
  borderRadius: radiusHalf(theme),
  fontWeight: 400,
  fontSize: 14,
  mx: 2,
});

export const getUserDrawerStyle = (isMobile: boolean) => ({
  width: isMobile ? '100%' : '30%',
  minWidth: isMobile ? 'auto' : 600,
  padding: isMobile ? 1 : 2,
  margin: isMobile ? 0 : 1,
  pt: 0,
  pb: 0,
  borderRadius: isMobile ? 0 : 2,
  height: isMobile ? '100%' : 'calc(100% - 16px)',
});

export const getShrinkLabelStyle = (theme: Theme): SystemStyleObject => ({
  position: 'absolute',
  fontSize: '0.90rem',
  color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.blox.colors.gray,
  background:
    theme.palette.mode === 'dark'
      ? `linear-gradient(to bottom, ${theme.blox.colors.medgray} 51%, transparent 0%)`
      : `linear-gradient(to bottom, ${theme.blox.colors.white} 51%, transparent 0%)`,
  fontWeight: 400,
  ml: 1.25,
  pl: 0.5,
  pr: 0.5,
  zIndex: 1000,
});

export const drawerFormContainerStyle = {
  pb: 2,
  pt: 2,
  '& .MuiInputBase-input': { fontSize: '16px' },
};

export const getDrawerFormHeaderStyle = (isMobile: boolean): SystemStyleObject => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  top: 0,
  position: 'sticky',
  zIndex: 10000,
  borderRadius: 0,
  p: 1,
  pl: 2,
  ml: isMobile ? -1 : -2, // Remove drawer space on header
  mr: isMobile ? -1 : -2,
  mb: isMobile ? 0 : 1,
});

export const getDrawerFormFooterStyle = (
  theme: Theme,
  isMobile: boolean,
): SystemStyleObject => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  bottom: 0,
  position: 'sticky',
  zIndex: 10000, // To overcome Tiptap link z-index
  borderRadius: 0,
  p: 1,
  ml: isMobile ? -1 : -2, // Remove drawer space on footer
  mr: isMobile ? -1 : -2,
  mb: isMobile ? 0 : -4,
});

export const userEditGridSx = (theme: Theme): SystemStyleObject => ({
  display: 'grid',
  minHeight: '100%', // For filling <Form> height and enabling sticky grid area so only one scroll area
  minWidth: '100%',
  height: '100%',
  width: '100%',
  overflow: 'auto',
  gridTemplateColumns: 'auto',
  gridTemplateRows: 'auto 1fr',
  gridTemplateAreas: `"actionbar" "contentscroll"`,
  backgroundColor: theme.palette.action.hover,

  // Container for our two flex cols so it can all scroll as one unit
  '#contentscroll': {
    gridArea: 'contentscroll',
    overflow: 'auto',
    display: 'grid',
    gap: spaceVal,
    py: spaceVal,
    gridTemplateRows: '1fr',
    gridTemplateColumns: 'minmax(300px, 35%) 1fr',
    gridTemplateAreas: '"leftcol rightcol"',
  },

  '#user-action-bar': {
    gridArea: 'actionbar',
  },

  '#layout-column-left': {
    display: 'flex',
    flexDirection: 'column',
    gridArea: 'leftcol',
    gap: spaceVal,
    pl: spaceVal,
  },

  '#layout-column-right': {
    gridArea: 'rightcol',
    display: 'flex',
    flexDirection: 'column',
    gap: spaceVal,
    pr: spaceVal,
  },

  // Assign order for mobile view using IDs - following some of the mockup order
  [theme.breakpoints.down('md')]: {
    '#layout-column-left, #layout-column-right': {
      display: 'contents', // Become direct children of #contentscroll
    },
    '#contentscroll': {
      display: 'flex', // Single flex col on mobile, order should work.
      flexDirection: 'column',
      px: spaceVal,
      gap: spaceVal,
    },
    '#useredit-wrap': {
      order: 1,
    },
    '#subscribe-wrap': {
      order: 2,
    },
    '#giftsubscriptions-wrap': {
      order: 3,
    },
    '#paymentmethods-wrap': {
      order: 4,
    },
    '#newsletters-wrap': {
      order: 5,
    },
    '#audiences-wrap': {
      order: 6,
    },
    '#activitystream-wrap': {
      order: 7,
    },
    '#linkedaccounts-wrap': {
      order: 8,
    },
  },
});

export const userCreateGridSx = (theme: Theme): SystemStyleObject => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'auto 1fr',
  gridTemplateAreas: `
  "actionbar"
  "usercreate"
  `,

  overflow: 'hidden',
  minHeight: '100%', // For filling <Form> height
  minWidth: '100%',
  height: '100%',
  width: '100%',

  '& #user-actionbar-area': {
    gridArea: 'actionbar',
  },

  '& #user-create-area': {
    gridArea: 'usercreate',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    p: spaceVal,
  },
});
